import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { metricDescriptionApi, type MetricDescription } from '../api/metricDescription';

type LoadState = 'idle' | 'loading' | 'found' | 'missing' | 'unavailable';

const results = new Map<string, MetricDescription>();
const pending = new Map<string, Promise<MetricDescription>>();
let availableNames: Set<string> | null = null;
let availabilityPending: Promise<Set<string>> | null = null;
const tooltipViewportPadding = 8;
const tooltipGap = 9;
const tooltipWidth = 360;

type TooltipPosition = {
  left: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

function loadDescription(name: string) {
  const cached = results.get(name);
  if (cached) return Promise.resolve(cached);
  const existing = pending.get(name);
  if (existing) return existing;
  const request = metricDescriptionApi.get(name)
    .then(response => response.data)
    .then(result => {
      results.set(name, result);
      return result;
    })
    .finally(() => pending.delete(name));
  pending.set(name, request);
  return request;
}

function loadAvailableNames() {
  if (availableNames) return Promise.resolve(availableNames);
  if (availabilityPending) return availabilityPending;
  availabilityPending = metricDescriptionApi.listAvailable()
    .then(response => new Set(response.data.names))
    .then(names => {
      availableNames = names;
      return names;
    })
    .finally(() => {
      availabilityPending = null;
    });
  return availabilityPending;
}

/**
 * Confirms that the metric has a server-owned definition before showing the
 * hint affordance. The full definition is still loaded only on interaction.
 */
export function MetricDescriptionTooltip({ label }: { label: string }) {
  const lookupName = label;
  const triggerRef = useRef<HTMLSpanElement>(null);
  const requestNameRef = useRef(lookupName);
  requestNameRef.current = lookupName;
  const cached = results.get(lookupName);
  const [, refreshAvailability] = useState(0);
  const [state, setState] = useState<LoadState>(cached?.found ? 'found' : cached ? 'missing' : 'idle');
  const [metric, setMetric] = useState<MetricDescription | null>(cached?.found ? cached : null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  useEffect(() => {
    let active = true;
    void loadAvailableNames()
      .then(() => {
        if (active) refreshAvailability(version => version + 1);
      })
      // Metric explanations are supplementary; a failed availability request
      // leaves the header as plain text without a non-functional affordance.
      .catch(() => {
        if (active) refreshAvailability(version => version + 1);
      });
    return () => {
      active = false;
    };
  }, [lookupName]);

  useEffect(() => {
    const next = results.get(lookupName);
    setState(next?.found ? 'found' : next ? 'missing' : 'idle');
    setMetric(next?.found ? next : null);
    setIsOpen(false);
  }, [lookupName]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const panelWidth = Math.min(tooltipWidth, viewportWidth - tooltipViewportPadding * 2);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, tooltipViewportPadding + panelWidth / 2),
      viewportWidth - tooltipViewportPadding - panelWidth / 2,
    );
    const spaceBelow = viewportHeight - rect.bottom - tooltipGap - tooltipViewportPadding;
    const spaceAbove = rect.top - tooltipGap - tooltipViewportPadding;
    if (spaceBelow < 180 && spaceAbove > spaceBelow) {
      setPosition({ left, bottom: viewportHeight - rect.top + tooltipGap, maxHeight: Math.max(0, spaceAbove) });
      return;
    }
    setPosition({ left, top: rect.bottom + tooltipGap, maxHeight: Math.max(0, spaceBelow) });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const requestDescription = useCallback(() => {
    if (state === 'loading' || state === 'found' || state === 'missing' || state === 'unavailable') return;
    setState('loading');
    void loadDescription(lookupName)
      .then(result => {
        if (requestNameRef.current !== lookupName) return;
        if (result.found && result.description) {
          setMetric(result);
          setState('found');
          return;
        }
        availableNames?.delete(lookupName);
        setState('missing');
        setIsOpen(false);
        refreshAvailability(version => version + 1);
      })
      // The tooltip is supplementary; preserve the table when its service is temporarily unavailable.
      .catch(() => {
        if (requestNameRef.current === lookupName) setState('unavailable');
      });
  }, [lookupName, state]);

  const openTooltip = useCallback(() => {
    updatePosition();
    setIsOpen(true);
    requestDescription();
  }, [requestDescription, updatePosition]);

  const isAvailable = availableNames?.has(lookupName) ?? false;
  if (!isAvailable) return <>{label}</>;

  return (
    <span
      ref={triggerRef}
      className="metric-header-tooltip metric-description-tooltip"
      onMouseEnter={openTooltip}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={openTooltip}
      onBlur={() => setIsOpen(false)}
      tabIndex={0}
    >
      <span>{label}</span>
      <span className="metric-header-tooltip-icon" aria-hidden="true">?</span>
      {isOpen && state !== 'idle' && position && createPortal(<span
        className="metric-header-tooltip-panel"
        role="tooltip"
        style={{ left: position.left, top: position.top, bottom: position.bottom, maxHeight: position.maxHeight }}
      >
        {state === 'found' ? <>
          <span className="metric-header-tooltip-title">{metric?.metric_name || lookupName}</span>
          <span className="metric-header-tooltip-row"><span className="metric-header-tooltip-label">口径描述</span><span className="metric-header-tooltip-content">{metric?.description}</span></span>
          <span className="metric-header-tooltip-row"><span className="metric-header-tooltip-label">口径负责人</span><span className="metric-header-tooltip-content">{metric?.metric_owner || '—'}</span></span>
          <span className="metric-header-tooltip-row"><span className="metric-header-tooltip-label">数仓负责人</span><span className="metric-header-tooltip-content">{metric?.warehouse_owner || '—'}</span></span>
        </> : <>
          <span className="metric-header-tooltip-title">{label}</span>
          <span className="metric-header-tooltip-message">
            {state === 'loading' ? '正在加载指标说明…' : state === 'missing' ? '暂无指标说明' : '指标说明暂不可用'}
          </span>
        </>}
      </span>, document.body)}
    </span>
  );
}
